import {HOLY,VARIANT_ART,PURGE_TEXTURES,PURGE_VARIANTS,PURGE_ASSETS,PURGE_FIRE_ASSET,purgeTextures,validPurgeAssets,validPurgeRecipe, type PurgeVariant} from '../shared/vfx-purge-profile';
export {PURGE_TEXTURES,PURGE_VARIANTS,PURGE_ASSETS,PURGE_FIRE_ASSET,purgeTextures,validPurgeAssets,validPurgeRecipe, type PurgeVariant} from '../shared/vfx-purge-profile';
import { bodyMotionPhase, burstFromPoint, convergeToPoint, createBodyBoundsTracker, spiralUp } from './ft3d-body-motion';

/** Already-installed, content-addressed art. The white central star is isolated in the shader;
 * no rune circle or water effect is drawn. Smoke_13's native alpha can darken the body. */

/** Same motion grammar and two fixed batches for the whole family. Shapes: star, flame, soft bloom,
 * silver star, droplet. Matter: native smoke, fine ash clusters, stretched curse filaments.
 * The absent variant retains the accepted shaders, numbers and buffers byte for byte. */

const TRAIL = 44, BURST = 20, DARK = 12, GLIMMER = 8, STREAKS = 14;
const BURST_START = 1 + TRAIL, BURN_START = BURST_START + BURST, GLIMMER_START = BURN_START + DARK;
const FLASH = GLIMMER_START + GLIMMER, STREAK_START = FLASH + 1, LIGHT = STREAK_START + STREAKS;
const vertex = `attribute vec4 purgeData;
 varying vec2 vUv; varying float vOpacity,vFrame,vKind;
 void main(){float a=purgeData.w,c=cos(a),s=sin(a);
  vUv=uv;vOpacity=purgeData.x;vFrame=purgeData.y;vKind=purgeData.z;
  vec4 centre=modelViewMatrix*instanceMatrix*vec4(0.,0.,0.,1.);
  vec2 size=vec2(length((modelViewMatrix*vec4(instanceMatrix[0].xyz,0.)).xyz),length((modelViewMatrix*vec4(instanceMatrix[1].xyz,0.)).xyz));
  #ifdef PURGE_LIGHT
   // 14 logical pixels at 1080p, scaled with viewport height. Perspective AND orthographic cameras.
   float pixels=vKind<.5?14.:(vKind<1.5?5.:0.);
   float clipW=abs((projectionMatrix*centre).w);
   float floorSize=pixels*2.*clipW/(1080.*projectionMatrix[1][1]);
   size=max(size,vec2(floorSize));
  #endif
  // GLSL mat2 is column-major: (c,s,-s,c) rotates by +a, so a streak placed at angle a points radially.
  centre.xy+=mat2(c,s,-s,c)*(position.xy*size);
  gl_Position=projectionMatrix*centre;}`;
const lightFragment = `uniform sampler2D map;uniform float warmth;
 varying vec2 vUv;varying float vOpacity,vKind;
 void main(){if(vOpacity<=0.)discard;vec2 p=vUv-.5;float r=length(p)*2.;
  float glow=exp(-4.8*r*r)*(1.-smoothstep(.78,1.,r));
  // The purchased star is a small core / occasional twinkle, never the shape of the flare.
  float star=texture2D(map,mix(vec2(.36),vec2(.64),clamp(p*2.6+.5,0.,1.))).r;
  star*=1.-smoothstep(.28,.40,r);
  bool twinkle=vKind<.5||(vKind>1.5&&vKind<3.5);
  float flare=step(2.5,vKind);
  vec3 gold=mix(vec3(1.,.74,.34),vec3(1.,.40,.09),warmth*flare);
  vec3 color=mix(gold,vec3(1.,.98,.89),twinkle?star:0.);
  float alpha=glow*.62+(twinkle?star*.9:0.);
  if(vKind>3.5&&vKind<4.5){alpha=glow*.94;color=mix(gold,vec3(1.,.95,.79),exp(-24.*r*r));}
  if(vKind>4.5){alpha=exp(-18.*p.y*p.y)*pow(max(0.,1.-abs(p.x)*2.),.5);color=mix(gold,vec3(1.,.96,.82),.35);}
  gl_FragColor=vec4(color,alpha*vOpacity);}`;
const smokeFragment = `uniform sampler2D map;
 varying vec2 vUv;varying float vOpacity,vFrame;
 void main(){if(vOpacity<=0.)discard;float f=clamp(floor(vFrame),0.,99.);
  vec2 cell=vec2(mod(f,10.),9.-floor(f/10.));
  vec2 q=(cell+mix(vec2(.035),vec2(.965),vUv))/10.;vec2 b=vec2(.0035,0.);
  float native=texture2D(map,q).a*.40;
  native+=(texture2D(map,q+b).a+texture2D(map,q-b).a+texture2D(map,q+b.yx).a+texture2D(map,q-b.yx).a)*.15;
  float r=length(vUv-.5)*2.;
  // Keep the native moving wisps, with enough density to read over a light body and a dark floor.
  float a=clamp(native*2.8+exp(-7.*r*r)*.12,0.,.94)*(1.-smoothstep(.78,1.,r));
  vec3 violet=mix(vec3(.24,.10,.33),vec3(.055,.025,.085),clamp(native*2.,0.,1.));
  gl_FragColor=vec4(violet,a*vOpacity);}`;

// Narrow flame/drop masks need a larger quad to retain an approximately 14 px visible lead.
const familyVertex = 'uniform float leadPixelFloor;\n' + vertex.replace('vKind<.5?14.', 'vKind<.5?leadPixelFloor');
const familyLightFragment = `uniform sampler2D map,fireMap;uniform float shape;
 uniform vec3 rim,core,lifeTint;
 varying vec2 vUv;varying float vOpacity,vFrame,vKind;
 void main(){if(vOpacity<=0.)discard;vec2 p=vUv-.5;float r=length(p)*2.;
  float glow=exp(-4.8*r*r)*(1.-smoothstep(.78,1.,r));
  float star=texture2D(map,mix(vec2(.36),vec2(.64),clamp(p*2.6+.5,0.,1.))).r*(1.-smoothstep(.28,.40,r));
  vec3 color=mix(rim,core,exp(-22.*r*r));float alpha=glow*.62;
  bool lead=vKind<.5,trail=vKind>.5&&vKind<2.5,flash=vKind>3.5&&vKind<4.5;
  if(shape<3.5){
   if(shape<1.5){
    float f=mod(floor(vFrame),36.);vec2 cell=vec2(mod(f,6.),5.-floor(f/6.));
    vec3 fire=texture2D(fireMap,(cell+mix(vec2(.006),vec2(.994),vUv))/6.).rgb;
    float flame=max(fire.r,max(fire.g,fire.b))*(1.-smoothstep(.88,1.,r));
    // Ember core stays small and bright; overlapping licks are the short continuous wake.
    alpha=glow*.42+flame*(flash?1.05:.75);color=mix(rim,core,max(exp(-22.*r*r),flame*.5));
   }else{
    if(shape<2.5){color=mix(color,lifeTint,smoothstep(.28,.85,r)*.22);alpha=glow*(flash?.72:.65);}
    if(lead||(!trail&&!flash&&vKind<4.5)){alpha+=star*.65;color=mix(color,core,star);}
   }
  }else{
   // Analytic tear with a clear blue edge and a small off-centre white reflection.
   vec2 q=vec2(p.x/(.32-.20*clamp(p.y+.35,0.,.75)),(p.y+.06)/.42);
   float drop=1.-smoothstep(.72,1.,length(q));
   float shine=exp(-180.*dot(p-vec2(-.065,.015),p-vec2(-.065,.015)));
   alpha=glow*(trail?.22:.30)+drop*.64+shine*.6;color=mix(rim,core,max(shine,drop*.24));
   if(flash){alpha=glow*.60;color=mix(rim,core,glow);}
  }
  if(vKind>4.5){alpha=exp(-18.*p.y*p.y)*pow(max(0.,1.-abs(p.x)*2.),.5);color=mix(rim,core,.45);}
  gl_FragColor=vec4(color,clamp(alpha,0.,1.)*vOpacity);}`;
const familyMatterFragment = `uniform sampler2D map;uniform float matter;
 uniform vec3 rim,core;varying vec2 vUv;varying float vOpacity,vFrame;
 void main(){if(vOpacity<=0.)discard;vec2 p=vUv-.5;float r=length(p)*2.;float a;
  if(matter<.5){float f=clamp(floor(vFrame),0.,99.);vec2 cell=vec2(mod(f,10.),9.-floor(f/10.));
   vec2 q=(cell+mix(vec2(.035),vec2(.965),vUv))/10.;vec2 b=vec2(.0035,0.);
   float native=texture2D(map,q).a*.40;
   native+=(texture2D(map,q+b).a+texture2D(map,q-b).a+texture2D(map,q+b.yx).a+texture2D(map,q-b.yx).a)*.15;
   a=clamp(native*2.8+exp(-7.*r*r)*.12,0.,.94)*(1.-smoothstep(.78,1.,r));
   gl_FragColor=vec4(mix(rim,core,clamp(native*2.,0.,1.)),a*vOpacity);return;
  }
  if(matter<1.5){
   // Seven thin, angular flakes per cluster. No density centre: soot is flaky, never gooey.
   vec2 q=p*3.;vec2 cell=floor(q+.5);vec2 f=fract(q+.5)-.5;
   float seed=sin(dot(cell,vec2(13.,37.))+vFrame*.02);
   float flake=max(abs(f.x+f.y*.45),abs(f.y-f.x*.4)*1.7);
   a=(1.-smoothstep(.17,.23,flake))*(1.-smoothstep(.65,1.,r))*step(-.75,seed);
  }else{
   // Jagged, unraveling filaments, stretched between the body sample and the passing light.
   float bend=sin(p.x*16.+vFrame*.12)*.13+sin(p.x*31.)*.035;
   a=(1.-smoothstep(.045,.11,abs(p.y-bend)))*(1.-smoothstep(.36,.5,abs(p.x)));
   a+= (1.-smoothstep(.05,.10,abs(p.y+bend*.65)))*.38*(1.-smoothstep(.25,.5,abs(p.x)));
  }
  gl_FragColor=vec4(mix(rim,core,.60),clamp(a,0.,1.)*vOpacity);}`;

/** Two batches; fixed buffers. Warm the same programs off-stage during recipe preload.
 * Bone offsets, paths and packed matrices update without allocations or vertex skinning. */
export function createPurchasedPurgeSpiral(T: any, world: any, root: any, position: any, height: number,
  maps: any[], identity: string, warmth = 0, variant?: PurgeVariant, caster = false) {
  const art = variant ? VARIANT_ART[variant] : HOLY;
  const group = new T.Group(); group.name = 'Cleanse body purge spiral'; group.userData.ftMagicVfx = true;
  const tracker = createBodyBoundsTracker(T, world, root); tracker.sample(position, height);
  const phase = bodyMotionPhase(identity);
  const scratch = new T.Vector3(), lead = new T.Vector3(), flare = new T.Vector3(), from = new T.Vector3();
  const direction = new T.Vector3(), right = new T.Vector3(1,0,0), up = new T.Vector3(0,1,0);
  const catchProgress = new Float32Array(DARK), directions = new Float32Array(BURST * 3);
  const batches = [LIGHT, DARK].map((count, i) => {
    const geometry = new T.PlaneGeometry(1, 1), data = new T.InstancedBufferAttribute(new Float32Array(count * 4), 4);
    data.setUsage(T.DynamicDrawUsage); geometry.setAttribute('purgeData', data);
    const uniforms: Record<string, { value: any }> = { map: { value: maps[i] }, warmth: { value: warmth } };
    // Keep accepted casts at their original uniform/allocation cost; only family shaders need these values.
    if (variant) Object.assign(uniforms, {
      leadPixelFloor: { value: art.leadFloor }, fireMap: { value: maps[2] ?? maps[0] },
      shape: { value: art.shape }, matter: { value: art.matter },
      rim: { value: new T.Vector3(...(i ? caster ? art.casterDarkRim : art.darkRim : art.rim)) },
      core: { value: new T.Vector3(...(i ? caster ? art.casterDarkCore : art.darkCore : art.core)) },
      lifeTint: { value: new T.Vector3(...art.tint) },
    });
    const material = new T.ShaderMaterial({ uniforms,
      defines: i ? {} : { PURGE_LIGHT: 1 },
      transparent: true, forceSinglePass: true, depthTest: true, depthWrite: false, side: T.DoubleSide, toneMapped: false,
      blending: i ? T.NormalBlending : T.AdditiveBlending, vertexShader: variant ? familyVertex : vertex,
      fragmentShader: variant ? i ? familyMatterFragment : familyLightFragment : i ? smokeFragment : lightFragment });
    material.name = i ? 'Cleanse purge smoke' : 'Cleanse purge light';
    const mesh = new T.InstancedMesh(geometry, material, count);
    mesh.instanceMatrix.setUsage(T.DynamicDrawUsage); mesh.frustumCulled = false; mesh.renderOrder = i ? 224 : 225;
    mesh.name = i ? 'Cleanse removable dark smoke' : 'Cleanse gold halo, glittering wake and radial flare';
    mesh.userData.purgeVariant = variant ?? 'holy'; group.add(mesh); return { mesh, geometry, material, data };
  });
  const initial = tracker.bounds;
  for (let i = 0; i < DARK; i++) {
    tracker.surfacePoint(from, i);
    // A height-local catch makes each wisp get drawn into the passing lead's wake.
    catchProgress[i] = Math.max(.24, Math.min(.88, ((from.y - initial.feetY) / initial.height - .065) / 1.085));
  }
  for (let i = 0; i < BURST; i++) {
    const a = phase.phase + i * 2.399963229728653, y = -.2 + (i % 5) * .26 + art.burstLift;
    direction.set(Math.cos(a), y, Math.sin(a)).normalize(); direction.toArray(directions, i * 3);
  }
  let disposed = false, progress = 0, flareAt = 0, margin = 0, currentLight = 0, currentDark = 0;
  const put = (batch: typeof batches[number], index: number, point: any, size: number,
    opacity: number, frame = 0, kind = 0, rotation = 0, aspect = 1): void => {
    // Billboard quads need only scale + translation. Avoid Matrix4.compose/setMatrixAt copying each sprite.
    const m = batch.mesh.instanceMatrix.array, at = index * 16;
    m[at] = size; m[at + 5] = size * aspect; m[at + 10] = size; m[at + 15] = 1;
    m[at + 12] = point.x; m[at + 13] = point.y; m[at + 14] = point.z;
    const data = batch.data.array, d = index * 4;
    data[d] = opacity; data[d + 1] = frame; data[d + 2] = kind; data[d + 3] = rotation;
    if (opacity > .001) { if (batch === batches[0]) currentLight++; else currentDark++; }
  };
  const update = (elapsed: number, duration: number, targetPosition: any, targetHeight: number, camera?: any): void => {
    if (disposed) return;
    const body = tracker.sample(targetPosition, targetHeight), h = body.height;
    const seconds = elapsed * 1.8 / duration, start = .2 + phase.delay, finish = 1.2 + phase.delay;
    progress = Math.max(0, Math.min(1, (seconds - start) / (finish - start)));
    margin = h * .065; flareAt = finish * duration / 1.8; currentLight = 0; currentDark = 0;
    group.visible = elapsed >= 0 && elapsed < duration;
    if (camera) { right.setFromMatrixColumn(camera.matrixWorld, 0).normalize(); up.setFromMatrixColumn(camera.matrixWorld, 1).normalize(); }
    spiralUp(lead, body, progress, phase.phase, phase.handedness, margin);
    spiralUp(flare, body, 1, phase.phase, phase.handedness, margin);
    const light = batches[0]!, dark = batches[1]!, active = group.visible ? 1 : 0;
    put(light, 0, lead, h * art.lead, active * Math.min(1, seconds / .10) * (seconds < finish ? 1 : 0), 3 + seconds * 22);
    // Dense overlapping glows, tapered in size/brightness; sparse stars interrupt the soft wake.
    for (let i = 0; i < TRAIL; i++) {
      const birth = Math.min(seconds, finish) - i / 250;
      const age = seconds - birth, u = (birth - start) / (finish - start), taper = Math.max(0, 1 - age / art.wake);
      const alpha = active && u >= 0 && u <= 1 && age >= 0 && age < art.wake ? taper * taper * .62 : 0;
      spiralUp(scratch, body, u, phase.phase, phase.handedness, margin + h * .003 * Math.sin(i * 2.4));
      put(light, 1 + i, scratch, h * (.038 + .060 * taper), alpha * (i % 9 === 5 ? .6 : 1), 3 + birth * 22 + i % 3, i % 9 === 5 ? 2 : 1, i * .7);
    }
    for (let i = 0; i < DARK; i++) {
      tracker.surfacePoint(from, i);
      const age = Math.max(0, seconds - i * .008), caught = start + catchProgress[i]!, a = i * 2.399963229728653;
      from.y += h * Math.min(.065, age * .09);
      from.x += Math.cos(a) * h * (.02 + Math.min(.045, age * .06));
      from.z += Math.sin(a) * h * (.02 + Math.min(.045, age * .06));
      const pulled = Math.max(0, Math.min(1, (seconds - caught) / .19));
      spiralUp(scratch, body, Math.max(0, progress - .018 * (i % 3)), phase.phase, phase.handedness, margin);
      let matterSize = h * (art.matterSize + i % 3 * art.matterStep) * (1 - pulled * .45);
      let matterAspect = art.matterAspect, matterRotation = a + age * .3;
      if (art.matter === 2) {
        const dx = (scratch.x-from.x)*right.x + (scratch.y-from.y)*right.y + (scratch.z-from.z)*right.z;
        const dy = (scratch.x-from.x)*up.x + (scratch.y-from.y)*up.y + (scratch.z-from.z)*up.z;
        matterSize = Math.max(h * .18, Math.hypot(dx,dy) * pulled);
        matterAspect = h * .055 / matterSize; matterRotation = Math.atan2(dy,dx);
        convergeToPoint(scratch, from, scratch, pulled * .5);
      } else convergeToPoint(scratch, from, scratch, pulled);
      const alpha = active && seconds < finish ? Math.min(1, age * 12) * .96 : 0;
      put(dark, i, scratch, matterSize, alpha,
        16 + (age * 32 + i * 3) % 58, 0, matterRotation, matterAspect);
      const burn = (seconds - finish) / .14;
      const bright = active && burn >= 0 && burn < 1 ? Math.sin(burn * Math.PI) * .9 : 0;
      scratch.copy(flare).addScaledVector(right, Math.cos(a) * h * .10 * burn).addScaledVector(up, Math.sin(a) * h * .10 * burn);
      put(light, BURN_START + i, scratch, h * .08, bright, 0, 3, a);
    }
    const burstAge = Math.max(0, seconds - finish), settling = Math.max(0, 1 - burstAge / (1.8 - finish));
    for (let i = 0; i < BURST; i++) {
      direction.fromArray(directions, i * 3);
      burstFromPoint(scratch, flare, direction, burstAge, h * (art.burstSpeed + (i % 4) * .14), h * art.gravity);
      const alpha = active && seconds >= finish ? Math.min(1, burstAge * 35) * settling ** 1.3 : 0;
      put(light, BURST_START + i, scratch, h * (.042 + (i % 3) * .012) * (.65 + settling * .35), alpha, 3 + burstAge * 65, 3, i * .7, art.burstAspect);
    }
    for (let i = 0; i < GLIMMER; i++) {
      tracker.surfacePoint(scratch, i); scratch.y += h * .01;
      const alpha = active && seconds >= finish + .12 ? settling * art.glimmerPower * (Math.sin(seconds * 17 + i) ** 2) : 0;
      put(light, GLIMMER_START + i, scratch, h * art.glimmerSize, alpha, 0, 2, i);
    }
    const flashAge = seconds - finish, flashAlpha = active && flashAge >= 0 && flashAge < art.flashLife ? Math.sin(flashAge / art.flashLife * Math.PI) : 0;
    put(light, FLASH, flare, h * (art.flashSize + Math.max(0, flashAge) * .8), flashAlpha * art.flashPower, 3 + Math.max(0,flashAge) * 100, 4);
    for (let i = 0; i < STREAKS; i++) {
      const a = i / STREAKS * Math.PI * 2 + phase.phase;
      const distance = h * (.10 + Math.max(0, flashAge) * .75);
      scratch.copy(flare).addScaledVector(right, Math.cos(a) * distance).addScaledVector(up, Math.sin(a) * distance);
      put(light, STREAK_START + i, scratch, h * (.07 + (i % 3) * .013), i < art.streaks ? flashAlpha * art.streakPower : 0, 0, 5, a, .18);
    }
    for (const batch of batches) { batch.mesh.instanceMatrix.needsUpdate = true; batch.data.needsUpdate = true; batch.mesh.visible = batch === dark ? currentDark > 0 : currentLight > 0; }
  };
  const dispose = (): void => {
    if (disposed) return; disposed = true; group.removeFromParent();
    for (const batch of batches) { batch.mesh.dispose(); batch.geometry.dispose(); batch.material.dispose(); }
  };
  return { root, group, parts: batches.map(b => b.mesh), geometries: batches.map(b => b.geometry), update, dispose,
    receipt: () => ({ disposed, phase: phase.phase, handedness: phase.handedness, delay: phase.delay, warmth, variant: variant ?? 'holy', caster,
      matterShape: art.matter, leadShape: art.shape, textureKeys: purgeTextures(variant),
      bounds: { ...tracker.bounds }, progress, flareAt, margin, lead: lead.toArray(), flare: flare.toArray(),
      visibleLight: currentLight, visibleDark: currentDark, quads: LIGHT + DARK, maxDrawCalls: 2,
      instanceBytes: (LIGHT + DARK) * (16 + 4) * 4, surfaceNodes: tracker.nodeCount, coreBoneCount: tracker.coreBoneCount, skinInfluenceBoxes: 0,
      leadSizeM: tracker.bounds.height * art.lead, starCoreHalfM: tracker.bounds.height * .044, leadFloorPx1080: art.leadFloor,
      catchProgress: Array.from(catchProgress) }) };
}
